import Grid from "@mui/material/Grid";
import { EmailContentType, Emptyable } from "@simplyk/common";
import classnames from "classnames";

import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useTranslate } from "../../../hooks/useTranslate";
import { useRecipientPlaceholder } from "../../EmailRecipientEditor/useRecipientPlaceholder";
import { useStyles } from "../useStyles";

import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardIconButton } from "@/dashboard-design-system/IconButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Expand, Cross as Close } from "@/icons/outlined";

interface EmailEditorHeaderProps {
  handleClose: () => void;
  recipientsCount: number;
  title?: Emptyable<string>;
  previewOpened?: boolean;
  openPreview?: () => void;
  closePreview?: () => void;
  emailType: Emptyable<EmailContentType>;
  hidePreviewButton?: boolean;
}

export const EmailEditorHeader = ({
  handleClose,
  recipientsCount,
  title,
  previewOpened,
  openPreview,
  closePreview,
  emailType,
  hidePreviewButton = false,
}: EmailEditorHeaderProps) => {
  const { isSmallScreen } = useMediaQuery();
  const { t } = useTranslate();
  const classes = useStyles();

  const recipientPlaceholder = useRecipientPlaceholder({ emailType, hasRecipients: Boolean(recipientsCount) });

  return (
    <Grid
      container
      className={classnames({ [classes.headerMobile]: isSmallScreen, [classes.headerDesktop]: !isSmallScreen })}
    >
      {!isSmallScreen && (
        <Grid item marginRight={1}>
          <DashboardIconButton
            onClick={handleClose}
            size="small"
            vibe="neutral"
            data-test="compose-email-write-email-drawer-close-button"
          >
            <Close />
          </DashboardIconButton>
        </Grid>
      )}
      <Grid item xs className={classnames({ [classes.modalTitleContainer]: !isSmallScreen })}>
        <DashboardTypography
          variant="h5"
          className={classnames({
            [classes.modalTitleDesktop]: !isSmallScreen,
            [classes.modalTitleMobile]: isSmallScreen,
          })}
        >
          {title ??
            // If there is a recipient placeholder, it means the recipients will be dynamically added to the email
            // We don't have to show the count
            (recipientPlaceholder
              ? t("dashboard", "emailEditor.createYourEmail")
              : t("dashboard", recipientsCount > 1 ? "tickets.sendToParticipants" : "tickets.sendToParticipant", {
                  count: recipientsCount,
                }))}
        </DashboardTypography>
      </Grid>
      {isSmallScreen && (
        <Grid item marginRight={1}>
          <DashboardIconButton
            onClick={handleClose}
            size="small"
            vibe="neutral"
            data-test="compose-email-write-email-drawer-close-button"
          >
            <Close />
          </DashboardIconButton>
        </Grid>
      )}
      {!isSmallScreen && !hidePreviewButton && (
        <Grid item marginRight={1}>
          <DashboardButton
            onClick={previewOpened ? closePreview : openPreview}
            size="medium"
            vibe="brand"
            variant="outlined"
            data-test="compose-email-write-email-drawer-close-button"
            endIcon={previewOpened ? <Close /> : <Expand />}
          >
            {t("dashboard", "preview")}
          </DashboardButton>
        </Grid>
      )}
    </Grid>
  );
};
